// src/tattoo/billing/PricingPage.jsx
// Public pricing page — /pricing — no auth required.
// Calls startCheckout() if the user is already signed in;
// otherwise routes to /signin so they can create an account first.
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { startCheckout } from "./api";
import { PLANS, TRIAL_DAYS, ARTIST_TRIAL_DAYS } from "./constants";

const C = {
  accent: "#c92d2d",
  bg: "#080808",
  bg2: "#0d0d0d",
  bg3: "#161616",
  border: "#1c1c1c",
  border2: "#282828",
  fg: "#eaeaea",
  fgDim: "#a8a8a8",
  fgMute: "#4a4a4a",
};

const S = {
  page: {
    background: C.bg,
    color: C.fg,
    fontFamily: "'Mulish', system-ui, -apple-system, sans-serif",
    minHeight: "100vh",
    WebkitFontSmoothing: "antialiased",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 40px",
    borderBottom: `1px solid ${C.border}`,
    background: "rgba(8,8,8,0.90)",
    backdropFilter: "blur(16px)",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  logoMark: { height: 30, width: "auto", opacity: 0.9 },
  navLinks: { display: "flex", alignItems: "center", gap: 8 },
  ghost: {
    fontSize: 13,
    fontWeight: 600,
    color: C.fgDim,
    textDecoration: "none",
    padding: "8px 14px",
    borderRadius: 7,
  },
  pill: {
    fontSize: 13,
    fontWeight: 700,
    color: C.fg,
    textDecoration: "none",
    padding: "8px 16px",
    borderRadius: 9999,
    border: `1px solid ${C.border2}`,
  },
  hero: {
    textAlign: "center",
    padding: "80px 24px 64px",
    background: `radial-gradient(ellipse 80% 50% at 50% 0%, #1f0808 0%, transparent 65%)`,
    borderBottom: `1px solid ${C.border}`,
  },
  eyebrow: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: C.accent,
    marginBottom: 16,
  },
  heroH1: {
    fontSize: "clamp(36px, 5vw, 60px)",
    fontWeight: 800,
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
    color: "#fff",
    marginBottom: 16,
  },
  heroSub: {
    fontSize: "clamp(15px, 1.8vw, 18px)",
    fontWeight: 300,
    color: C.fgDim,
    lineHeight: 1.6,
    marginBottom: 48,
  },
  // free banner
  freeBanner: {
    maxWidth: 680,
    margin: "0 auto",
    background: C.bg2,
    border: `1px solid ${C.border2}`,
    borderRadius: 16,
    padding: "28px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    flexWrap: "wrap",
  },
  freeLeft: { display: "flex", flexDirection: "column", gap: 6 },
  freeLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: C.fgMute,
  },
  freeName: { fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" },
  freeDesc: { fontSize: 14, color: C.fgDim, lineHeight: 1.5, maxWidth: 380 },
  freeFeats: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    marginTop: 4,
    textAlign: "left",
  },
  freeFeat: {
    fontSize: 13,
    color: C.fgDim,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  freeDot: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: C.fgMute,
    flexShrink: 0,
  },
  btnRed: {
    display: "inline-block",
    padding: "12px 24px",
    background: C.accent,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    textDecoration: "none",
    borderRadius: 9999,
    border: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
  },
  // section
  section: { padding: "72px 24px" },
  sectionAlt: {
    padding: "72px 24px",
    background: C.bg2,
    borderTop: `1px solid ${C.border}`,
  },
  wrap: { maxWidth: 1040, margin: "0 auto" },
  sectionHead: { marginBottom: 40 },
  audienceTag: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: C.fgMute,
    marginBottom: 10,
  },
  sectionH2: {
    fontSize: "clamp(24px, 3.5vw, 38px)",
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1.12,
    marginBottom: 8,
  },
  sectionSub: {
    fontSize: 15,
    color: C.fgDim,
    fontWeight: 300,
    lineHeight: 1.6,
  },
  // cards
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 2,
    background: C.border,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    overflow: "hidden",
  },
  card: {
    background: C.bg,
    padding: "32px 28px",
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  cardFeatured: {
    background: "#0f0707",
    padding: "32px 28px",
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  cardBadge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#fff",
    background: C.accent,
    padding: "3px 10px",
    borderRadius: 9999,
    width: "fit-content",
    marginBottom: 16,
  },
  // Floating variant — sits on the top keyline of the outer cards
  // rectangle rather than inside the featured card. Used by the
  // "Most popular" badge so it bisects the top border instead of
  // taking vertical space inside the middle card and shoving its
  // price row out of alignment with the side cards' price rows.
  cardBadgeFloat: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 10,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#fff",
    background: C.accent,
    padding: "5px 14px",
    borderRadius: 9999,
    whiteSpace: "nowrap",
  },
  cardName: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    marginBottom: 4,
  },
  cardName2: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    marginBottom: 4,
    marginTop: 6,
  },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    margin: "16px 0 4px",
  },
  priceAmt: {
    fontSize: 38,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  priceCad: { fontSize: 14, color: C.fgDim, fontWeight: 400 },
  trialNote: { fontSize: 12, color: C.fgMute, marginBottom: 20, minHeight: 18 },
  divider: { height: 1, background: C.border2, margin: "20px 0" },
  feats: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    flex: 1,
    marginBottom: 24,
  },
  featRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    fontSize: 13.5,
    color: C.fgDim,
    lineHeight: 1.4,
  },
  check: { color: C.accent, flexShrink: 0, fontSize: 14, marginTop: 1 },
  cardBtn: {
    display: "block",
    width: "100%",
    padding: "12px 20px",
    background: C.accent,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    textAlign: "center",
    textDecoration: "none",
    borderRadius: 9999,
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  cardBtnGhost: {
    display: "block",
    width: "100%",
    padding: "12px 20px",
    background: "transparent",
    color: C.fg,
    fontSize: 14,
    fontWeight: 600,
    textAlign: "center",
    textDecoration: "none",
    borderRadius: 9999,
    border: `1px solid ${C.border2}`,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  // savings tag
  savingsTag: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#4ade80",
    background: "rgba(74,222,128,0.1)",
    padding: "3px 8px",
    borderRadius: 4,
    marginLeft: 8,
  },
  // footer
  footer: {
    borderTop: `1px solid ${C.border}`,
    padding: "28px 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
  },
  footerWordmark: { height: 20, width: "auto", opacity: 0.3 },
  footerLinks: { display: "flex", gap: 24, alignItems: "center" },
  footerLink: { fontSize: 12, color: C.fgMute, textDecoration: "none" },
  // note
  noteBox: {
    maxWidth: 680,
    margin: "40px auto 0",
    fontSize: 13,
    color: C.fgMute,
    lineHeight: 1.6,
    textAlign: "center",
  },

  artistOutcomes: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 2,
    background: C.border,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    overflow: "hidden",
    margin: "0 0 36px",
  },
  artistOutcome: {
    background: C.bg,
    padding: "22px 20px",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },

  artistOutcomeCheck: {
    color: C.accent,
    width: 20,
    flexShrink: 0,
    fontSize: 18,
    lineHeight: 1.3,
  },

  artistOutcomeText: {
    fontSize: 14,
    fontWeight: 700,
    color: C.fg,
    lineHeight: 1.45,
  },
};

function PlanButton({ planId, label, style }) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function handleClick() {
    if (!session) {
      navigate("/signin");
      return;
    }
    try {
      setBusy(true);
      await startCheckout(planId);
    } catch (e) {
      console.error("[pricing] checkout error:", e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      style={{ ...style, opacity: busy ? 0.6 : 1 }}
    >
      {busy ? "Loading…" : label}
    </button>
  );
}

function Check() {
  return <span style={S.check}>✓</span>;
}

export default function PricingPage() {
  return (
    <div style={S.page}>
      <link
        href="https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300&display=swap"
        rel="stylesheet"
      />

      <style>{`
  @media (max-width: 760px) {
    .pricing-outcomes {
      grid-template-columns: 1fr !important;
    }
  }
`}</style>

      {/* ── NAV ──────────────────────────────────────────────── */}
      <nav style={S.nav}>
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
          }}
        >
          <img src="/tattune-logo-grey.svg" alt="Tattune" style={S.logoMark} />
        </Link>
        <div style={S.navLinks}>
          <Link to="/artists" style={S.ghost}>
            Browse artists
          </Link>
          <Link to="/signin" style={S.pill}>
            Sign in →
          </Link>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────── */}
      <div style={S.hero}>
        <span style={S.eyebrow}>Pricing</span>
        <h1 style={S.heroH1}>
          Start free.
          <br />
          Go deeper when you're ready.
        </h1>
        <p style={S.heroSub}>
          Try the Plannequin and browse artists at no cost.
          <br />
          Upgrade when you want to save, share, or get found.
        </p>

        {/* Free tier banner */}
        <div style={S.freeBanner}>
          <div style={S.freeLeft}>
            <span style={S.freeLabel}>Always free</span>
            <span style={S.freeName}>Tattune Free</span>
            <div style={S.freeFeats}>
              {[
                "Try the Plannequin — place, scale, rotate, morph",
                "Browse artist directories and flash libraries",
                "View artist profiles and portfolios",
              ].map((f) => (
                <div key={f} style={S.freeFeat}>
                  <div style={S.freeDot} />
                  {f}
                </div>
              ))}
            </div>
          </div>
          <Link to="/app" style={S.btnRed}>
            Get started free →
          </Link>
        </div>
      </div>

      {/* ── CLIENT PLANS ─────────────────────────────────────── */}
      <section style={S.section}>
        <div style={S.wrap}>
          <div style={S.sectionHead}>
            <span style={S.audienceTag}>For ink lovers</span>
            <h2 style={S.sectionH2}>Save your work. Share your vision.</h2>
            <p style={S.sectionSub}>
              Active Tattune subscribers get full access to every artist's flash
              library — and artists notice the clients who already know what
              they want.
            </p>
          </div>

          {/* Wrap the cards in a positioning context so the
              "Most popular" badge can float on the top edge of the
              outer rounded rectangle, rather than sitting inside the
              middle card (where it took vertical space and pushed the
              middle price row below the side cards' price rows — the
              "dip" Shep flagged). Single source: the wrapper.  The
              badge is auto-centred on the middle card because all
              three cards share the same column width in the auto-fit
              grid. */}
          <div style={{ position: "relative" }}>
            <span style={S.cardBadgeFloat}>Most popular</span>

            <div style={S.cards}>
              {/* Single Design */}
              <div style={S.card}>
                <div style={S.cardName}>Single Design</div>
                <div style={S.priceRow}>
                  <span style={S.priceAmt}>$4.99</span>
                  <span style={S.priceCad}>one-time</span>
                </div>
                <p style={S.trialNote}>No subscription, no expiry.</p>
                <div style={S.divider} />
                <div style={S.feats}>
                  {[
                    "Design and save one tattoo project",
                    "Full Plannequin access — morph, scale, rotate",
                    "Share your placement with your artist",
                    "Great for a one-off or second-tat top-up",
                  ].map((f) => (
                    <div key={f} style={S.featRow}>
                      <Check />
                      {f}
                    </div>
                  ))}
                </div>
                <PlanButton
                  planId="single_design"
                  label="Buy one design"
                  style={S.cardBtnGhost}
                />
              </div>

              {/* Customer Pro monthly — badge moved to the floating
                position on the wrapper above. */}
              <div style={S.cardFeatured}>
                <div style={S.cardName}>Customer Pro</div>
                <div style={S.priceRow}>
                  <span style={S.priceAmt}>$9.99</span>
                  <span style={S.priceCad}>/mo</span>
                </div>
                <p style={S.trialNote}>
                  {TRIAL_DAYS}-day free trial — no card required.
                </p>
                <div style={S.divider} />
                <div style={S.feats}>
                  {[
                    "Unlimited saved designs",
                    "HD exports",
                    "Full flash gallery access across all studios",
                    "Morph & share Plannequin placements",
                    "Cancel anytime",
                  ].map((f) => (
                    <div key={f} style={S.featRow}>
                      <Check />
                      {f}
                    </div>
                  ))}
                </div>
                <PlanButton
                  planId="customer_pro"
                  label={`Start ${TRIAL_DAYS}-day free trial`}
                  style={S.cardBtn}
                />
              </div>

              {/* Customer Pro yearly */}
              <div style={S.card}>
                <div style={S.cardName}>
                  Customer Pro
                  <span style={S.savingsTag}>Save 25%</span>
                </div>
                <div style={S.priceRow}>
                  <span style={S.priceAmt}>$89.99</span>
                  <span style={S.priceCad}>/yr</span>
                </div>
                <p style={S.trialNote}>~$7.50/mo — billed once a year.</p>
                <div style={S.divider} />
                <div style={S.feats}>
                  {[
                    "Everything in Customer Pro monthly",
                    "~25% savings vs monthly",
                    "Best for collectors planning their next piece",
                  ].map((f) => (
                    <div key={f} style={S.featRow}>
                      <Check />
                      {f}
                    </div>
                  ))}
                </div>
                <PlanButton
                  planId="customer_pro_yearly"
                  label="Get the yearly plan"
                  style={S.cardBtnGhost}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ARTIST PLANS ─────────────────────────────────────── */}
      <section style={S.sectionAlt}>
        <div style={S.wrap}>
          <div style={S.sectionHead}>
            <span style={S.audienceTag}>For artists & studios</span>
            <h2 style={S.sectionH2}>Get found. Get booked.</h2>
            <p style={S.sectionSub}>
              List your flash, receive direct client inquiries, and connect with
              people who already know what they want — and where they want it.
            </p>
          </div>

          <div className="pricing-outcomes" style={S.artistOutcomes}>
            {[
              "Reduce placement revisions",
              "Qualify clients before consultations",
              "Share visual concepts instead of screenshots",
              "Turn flash browsers into inquiries",
            ].map((item) => (
              <div key={item} style={S.artistOutcome}>
                <span style={S.artistOutcomeCheck}>✓</span>
                <span style={S.artistOutcomeText}>{item}</span>
              </div>
            ))}
          </div>

          <div
            style={{
              ...S.cards,
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            }}
          >
            {/* Artist Pro monthly */}
            <div style={S.cardFeatured}>
              <span style={S.cardBadge}>Artist Pro</span>
              <div style={S.cardName}>Monthly</div>
              <div style={S.priceRow}>
                <span style={S.priceAmt}>$19</span>
                <span style={S.priceCad}>/mo</span>
              </div>
              <p style={S.trialNote}>
                {ARTIST_TRIAL_DAYS}-day free trial — build your profile first.
              </p>
              <div style={S.divider} />
              <div style={S.feats}>
                {[
                  "Public studio profile in the Tattune directory",
                  "Upload your full flash gallery",
                  "Lead inbox — client inquiries come directly to you",
                  "Full Plannequin access (Customer Pro included)",
                  "Cancel anytime",
                ].map((f) => (
                  <div key={f} style={S.featRow}>
                    <Check />
                    {f}
                  </div>
                ))}
              </div>
              <PlanButton
                planId="artist_pro"
                label={`Start ${ARTIST_TRIAL_DAYS}-day free trial`}
                style={S.cardBtn}
              />
            </div>

            {/* Artist Pro yearly */}
            <div style={S.card}>
              <div style={S.cardName}>
                Artist Pro
                <span style={S.savingsTag}>Save ~17%</span>
                <div style={S.cardName2}>Yearly</div>
              </div>
              <div style={S.priceRow}>
                <span style={S.priceAmt}>$190</span>
                <span style={S.priceCad}>/yr</span>
              </div>
              <p style={S.trialNote}>~$15.83/mo — billed once a year.</p>
              <div style={S.divider} />
              <div style={S.feats}>
                {[
                  "Everything in Artist Pro monthly",
                  "~17% savings vs monthly",
                  "Commit once — stay discoverable all year",
                ].map((f) => (
                  <div key={f} style={S.featRow}>
                    <Check />
                    {f}
                  </div>
                ))}
              </div>
              <PlanButton
                planId="artist_pro_yearly"
                label="Get the yearly plan"
                style={S.cardBtnGhost}
              />
            </div>
          </div>

          <p style={S.noteBox}>
            Artist Pro includes everything in Customer Pro. Subscribers who
            browse your flash are active Tattune users — they already know how
            to use the Plannequin and they're ready to book.
          </p>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer style={S.footer}>
        <img
          src="/tattune-tagline-grey.svg"
          alt="Tattune"
          style={S.footerWordmark}
        />
        <div style={S.footerLinks}>
          <Link to="/" style={S.footerLink}>
            Home
          </Link>
          <Link to="/artists" style={S.footerLink}>
            Browse artists
          </Link>
          <Link to="/privacy" style={S.footerLink}>
            Privacy
          </Link>
          <Link to="/signin" style={S.footerLink}>
            Sign in
          </Link>
        </div>
      </footer>
    </div>
  );
}
